import Flickity from 'flickity';

import debounce from '../util/debounce';
import QuickViewPopup from '../features/quick-view-popup';
import {register} from '../vendor/theme-scripts/theme-sections';

const sections = {};

const selectors = {
  slider: '[data-slider]',
  sliderItem: '[data-slide-item]',
  pointer: '[data-pointer]',
  productGridItemImage: '[data-product-media-container]',
  quickViewItemHolder: '[data-quick-view-item-holder]',
  flickityButton: '.flickity-button',
  links: 'a, button',
};

const attributes = {
  pointer: 'data-pointer',
  hotspot: 'data-hotspot',
  tabIndex: 'tabindex',
};

const classes = {
  pointerSelected: 'pointer--selected',
  isSelected: 'is-selected',
  isActive: 'is-active',
  popupOpen: 'pswp--open',
};

class Look {
  constructor(section) {
    this.container = section.container;
    this.slider = this.container.querySelector(selectors.slider);
    this.slides = this.container.querySelectorAll(selectors.sliderItem);
    this.pointers = this.container.querySelectorAll(selectors.pointer);
    this.flkty = null;

    this.checkSlidesSizeOnResize = () => this.checkSlidesSize();
    this.pointersInit = (event) => this.dotPointers(event);
    this.pointersOver = () => this.dotPointerIn();
    this.pointersOut = () => this.dotPointerOut();

    this.debouncedBlockSelectCallback = debounce((event) => this.debouncedBlockSelect(event), 500);

    this.quickViewPopup = new QuickViewPopup(this.container);
    this.listen();
  }

  listen() {
    if (this.slider) {
      this.checkSlidesSize();
      document.addEventListener('theme:resize:width', this.checkSlidesSizeOnResize);
    }

    if (this.pointers.length > 0) {
      this.pointers.forEach((pointer) => {
        pointer.addEventListener('click', this.pointersInit);
        pointer.addEventListener('mouseover', this.pointersOver);
        pointer.addEventListener('mouseleave', this.pointersOut);
      });
    }
  }

  checkSlidesSize() {
    const isDesktop = window.innerWidth >= theme.sizes.small;

    if (isDesktop) {
      if (this.slides.length > 2) {
        this.initSlider();
      } else {
        this.destroySlider();
        this.slidesTabIndex();
      }

      return;
    }

    if (!isDesktop && this.slides.length > 1) {
      this.initSlider();

      return;
    }

    this.destroySlider();
  }

  initSlider() {
    if (this.flkty === null) {
      this.flkty = new Flickity(this.slider, {
        prevNextButtons: true,
        wrapAround: true,
        adaptiveHeight: false,
        cellAlign: 'left',
        groupCells: false,
        contain: true,
        on: {
          ready: () => {
            this.slidesTabIndex();
            this.setSliderArrowsPosition();
            this.dotPointers();
          },
          change: () => {
            this.slidesTabIndex();
            this.dotPointers();
          },
        },
      });

      return;
    }

    this.setSliderArrowsPosition();
  }

  setSliderArrowsPosition() {
    const isDesktop = window.innerWidth >= theme.sizes.small;

    if (!isDesktop) return;

    const arrows = this.slider.querySelectorAll(selectors.flickityButton);
    const image = this.slider.querySelector(selectors.productGridItemImage);

    if (arrows.length && image) {
      arrows.forEach((arrow) => {
        arrow.style.top = `${image.offsetHeight / 2}px`;
      });
    }
  }

  slidesTabIndex() {
    if (this.slides.length < 3) {
      this.slider.querySelectorAll(selectors.links).forEach((link) => {
        link.setAttribute(attributes.tabIndex, '0');
      });

      return;
    }

    const slider = Flickity.data(this.slider);

    slider.cells.forEach((slide) => {
      slide.element.querySelectorAll(selectors.links).forEach((link) => {
        link.setAttribute(attributes.tabIndex, '-1');
      });
    });

    slider.cells.forEach((slide) => {
      if (slide.element.classList.contains(classes.isSelected)) {
        slide.element.querySelectorAll(selectors.links).forEach((link) => {
          link.setAttribute(attributes.tabIndex, '0');
        });

        // Used to add tabindex = 0 to the other slide element that's visible as well, but is not marked as selected
        const secondaryElement = slide.element.nextSibling ? slide.element.nextSibling : slide.element.parentNode.firstChild;

        secondaryElement.querySelectorAll(selectors.links).forEach((link) => {
          link.setAttribute(attributes.tabIndex, '0');
        });
      }
    });
  }

  destroySlider() {
    if (typeof this.flkty === 'object' && this.flkty !== null) {
      this.flkty.destroy();
      this.flkty = null;
    }
  }

  dotPointers(event) {
    if (this.pointers.length === 0) return;

    this.pointers.forEach((button) => {
      button.classList.remove(classes.pointerSelected);
    });

    if (event) {
      const dotIndex = event.target.getAttribute(attributes.pointer);

      this.flkty?.select(dotIndex);

      return;
    }

    const slideIndex = this.flkty == null ? 0 : this.flkty.selectedIndex;

    if (slideIndex >= 0) {
      this.pointers[slideIndex].classList.add(classes.pointerSelected);
    }
  }

  dotPointerIn() {
    this.pointers.forEach((pointer) => {
      pointer.style.setProperty('--look-animation', 'none');
    });
  }

  dotPointerOut() {
    this.pointers.forEach((pointer) => {
      pointer.style.removeProperty('--look-animation');
    });
  }

  triggerClick(target) {
    requestAnimationFrame(() => target.dispatchEvent(new Event('click')));
  }

  destroyQuickViewPopup() {
    const pswpElement = this.quickViewPopup?.loadPhotoswipe?.pswpElement;
    if (!pswpElement) return;
    if (pswpElement.classList.contains(classes.popupOpen)) {
      this.quickViewPopup.loadPhotoswipe.popup.close();
    }
  }

  /**
   * Event callback for Theme Editor `shopify:block:select` event
   * The timeouts here are necessary for issues with selecting blocks from one `Shop the look` section to another
   */
  onBlockSelect(event) {
    this.debouncedBlockSelectCallback(event);
  }

  debouncedBlockSelect(event) {
    const pswpElement = this.quickViewPopup?.loadPhotoswipe?.pswpElement;

    // No popup element
    if (!pswpElement) {
      setTimeout(() => this.triggerClick(event.target), 400);
      return;
    }

    setTimeout(() => {
      // Popup initialized
      if (pswpElement.classList.contains(classes.popupOpen)) {
        // Popup opened
        const holder = this.quickViewPopup.loadPhotoswipe.pswpElement.querySelector(`[${attributes.hotspot}="${event.target.getAttribute(attributes.hotspot)}"]`);
        const quickViewItemHolders = this.quickViewPopup.loadPhotoswipe.pswpElement.querySelectorAll(selectors.quickViewItemHolder);

        holder.classList.add(classes.isActive);

        quickViewItemHolders.forEach((element) => {
          if (element !== holder) {
            element.classList.remove(classes.isActive);
          }
        });
      } else {
        // Popup closed
        this.triggerClick(event.target);
      }
    });
  }

  /**
   * Event callback for Theme Editor `shopify:section:unload` event
   */
  onUnload() {
    this.destroyQuickViewPopup();
    document.removeEventListener('theme:resize:width', this.checkSlidesSizeOnResize);
  }

  /**
   * Event callback for Theme Editor `shopify:section:deselect` event
   */
  onDeselect() {
    this.destroyQuickViewPopup();
  }
}

const lookSection = {
  onLoad() {
    sections[this.id] = new Look(this);
  },
  onUnload() {
    sections[this.id].onUnload();
  },
  onBlockSelect(event) {
    sections[this.id].onBlockSelect(event);
  },
  onDeselect() {
    sections[this.id].onDeselect();
  },
};

register('look', lookSection);
